import { useContext } from 'react'
import { useSelector } from 'react-redux'

import {
  useForm, useLatestEntity, useThunkDispatch,
} from '@domain-group/react-hooks'

import defaultState, { requiredFields } from '@models/providerService'

import * as providerServiceActions from '@redux/modules/providerService'

import PageContext from '@contexts/pageContext'

const createOrEditProviderService = params => new Promise((resolve, reject) => {
  const { providerService, showCreateOrEditProviderServiceModal } = params

  if (showCreateOrEditProviderServiceModal){
    const payload = {
      providerService,
    }

    showCreateOrEditProviderServiceModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditProviderServiceModal not defined in PageContext callbacks'))
})

const createProviderService = (params) => {
  const { dispatch, providerServiceParams, requestOptions } = params
  const { createProviderService: createFn } = providerServiceActions

  return dispatch(createFn(providerServiceParams, requestOptions))
}

const updateProviderService = (params) => {
  const {
    dispatch, providerService, providerServiceParams, requestOptions,
  } = params
  const { updateProviderService: updateFn } = providerServiceActions

  const updatedParams = {
    id: providerService.id,
    ...providerServiceParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

const deleteProviderService = (params) => {
  const { providerService, dispatch } = params
  const { deleteProviderService: deleteFn } = providerServiceActions

  return dispatch(deleteFn(providerService))
}

export const useRelations = (providerService) => {
  const { provider_id, service_id } = providerService

  const { providers, services } = useSelector(reduxState => reduxState.entities)

  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const service = service_id && services[service_id] ? services[service_id] : {}

  return {
    provider,
    service,
  }
}

export function useProviderServiceForm(providerService = {}, options = {}){
  const { validateOn } = options

  const providerServiceForm = useForm(
    defaultState,
    { entity: providerService, requiredFields, validateOn },
    [providerService.id, providerService.cache_key],
  )

  return {
    ...providerServiceForm,
  }
}

const useProviderService = (initEntity = {}) => {
  const { entity: providerService } = useLatestEntity(initEntity, 'providerServices')

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditProviderServiceModal } = callbacks || {}

  const { creating, deleting, updating } = useSelector(reduxState => reduxState.providerServices)

  const dispatch = useThunkDispatch()

  const { provider, service } = useRelations(providerService)

  return {
    callbacks: {
      createOrEditProviderService: () => (
        createOrEditProviderService({ providerService, showCreateOrEditProviderServiceModal })
      ),
      createProviderService: (providerServiceParams, requestOptions) => (
        createProviderService({ providerServiceParams, dispatch, requestOptions })
      ),
      updateProviderService: (providerServiceParams, requestOptions) => updateProviderService({
        providerService, providerServiceParams, dispatch, requestOptions,
      }),
      deleteProviderService: () => deleteProviderService({ providerService, dispatch }),
    },
    creating,
    deleting,
    provider,
    providerService,
    service,
    updating,
  }
}

export default useProviderService
